import type { NoteModelData } from "@/store/note/types";
import { MEM_SPACE_ACCOUNT_ID } from "@/store/contacts/constants";

export const MEM_INGESTION_ECHO_BUFFER_MS = 60 * 1000;

// Id/timestamp fields widened to allow null/undefined so the same shape can be
// built from a NoteObservable (non-null) or a server artifact payload
// (nullable). Every field is guarded below, so widening is safe.
type EchoModelData = {
  [K in
    | "attributed_created_by_space_account_id"
    | "created_by_space_account_id"
    | "owned_by_space_account_id"
    | "last_modified_by_space_account_id"
    | "locally_modified_at"
    | "quota_created_at"
    | "locally_created_at"
    | "created_at"]?: NoteModelData[K] | null;
};

// Mirrors NoteObservable.attributedCreatorSpaceAccountId's fallback chain.
const getAttributedCreatorSpaceAccountId = (modelData: EchoModelData): string | undefined =>
  modelData.attributed_created_by_space_account_id ??
  modelData.created_by_space_account_id ??
  modelData.owned_by_space_account_id ??
  undefined;

// Mirrors NoteObservable.quotaCreatedAt's fallback chain.
const getQuotaCreatedAt = (modelData: EchoModelData): string | undefined =>
  modelData.quota_created_at || modelData.locally_created_at || modelData.created_at || undefined;

// True iff the latest write on this note is a Mem-on-Mem same-actor follow-up
// that landed within MEM_INGESTION_ECHO_BUFFER_MS of quota_created_at — e.g.
// titling, cleanup, or embedding passes that fire immediately after Mem
// creates the note. Treated as part of creation, not a real edit.
export const isMemIngestionEchoForModelData = (modelData: EchoModelData): boolean => {
  const editedAt = modelData.locally_modified_at;
  if (!editedAt) return false;
  if (getAttributedCreatorSpaceAccountId(modelData) !== MEM_SPACE_ACCOUNT_ID) return false;
  if (modelData.last_modified_by_space_account_id !== MEM_SPACE_ACCOUNT_ID) return false;
  const quotaCreatedAt = getQuotaCreatedAt(modelData);
  if (!quotaCreatedAt) return false;
  return (
    new Date(editedAt).getTime() <=
    new Date(quotaCreatedAt).getTime() + MEM_INGESTION_ECHO_BUFFER_MS
  );
};

// Sibling of isMemIngestionEchoForModelData for collection-membership adds.
// True iff Mem created the note AND Mem added it to the collection within
// MEM_INGESTION_ECHO_BUFFER_MS of quota_created_at — i.e. the membership add
// is part of the same ingestion act as creation, not a separate "edited" event.
export const isMemIngestionEchoForCollectionAdd = (params: {
  noteModelData: EchoModelData;
  addedBySpaceAccountId: string | null | undefined;
  addedAt: string;
}): boolean => {
  if (params.addedBySpaceAccountId !== MEM_SPACE_ACCOUNT_ID) return false;
  if (getAttributedCreatorSpaceAccountId(params.noteModelData) !== MEM_SPACE_ACCOUNT_ID) {
    return false;
  }
  const quotaCreatedAt = getQuotaCreatedAt(params.noteModelData);
  if (!quotaCreatedAt) return false;
  return (
    new Date(params.addedAt).getTime() <=
    new Date(quotaCreatedAt).getTime() + MEM_INGESTION_ECHO_BUFFER_MS
  );
};
